import { useEffect, useRef, useState } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

type SortSelectOption<V extends string> = { value: V; label: string };

/**
 * Inline "Sort by {label} ⌄" control (Figma sort row). A lightweight popover
 * menu — UI machinery, so it owns its open/close state locally.
 */
function SortSelect<V extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: V;
  options: ReadonlyArray<SortSelectOption<V>>;
  onChange: (value: V) => void;
  label: string;
  className?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent): void {
      if (
        ref.current !== null &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        className="flex items-center gap-1 text-footnote whitespace-nowrap"
      >
        <span className="font-normal text-ds-slate-600">{label}</span>
        <span className="font-medium text-ds-black">
          {selected === undefined ? "" : selected.label}
        </span>
        <IconChevronDown
          size={16}
          className={cn(
            "text-ds-slate-600 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 flex flex-col overflow-hidden rounded-2xl border border-ds-black/10 bg-white py-1 shadow-popover">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={cn(
                "whitespace-nowrap px-4 py-2 text-left text-footnote hover:bg-ds-slate-50",
                option.value === value
                  ? "font-medium text-ds-black"
                  : "font-normal text-ds-slate-700"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { SortSelect, type SortSelectOption };
